import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import notificationPushService from "../../services/notificationPush.service";

const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

async function loadFirebaseMessaging(config, registration) {
  const { initializeApp, getApps } = await import("firebase/app");
  const { getMessaging, getToken, onMessage, isSupported } = await import("firebase/messaging");
  if (!(await isSupported())) return null;
  const app = getApps().length
    ? getApps()[0]
    : initializeApp({
        apiKey: config.apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        messagingSenderId: config.messagingSenderId,
        appId: config.appId,
      });
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: registration,
  });
  return { messaging, token, onMessage };
}

export default function PushNotificationBootstrap() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const tokenRef = useRef("");
  const isAdmin = auth?.user?.role === "REPO_ADMIN" && Boolean(auth?.token);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type !== "FCM_OPEN") return;
      const path = String(event.data.path || "");
      if (path.startsWith("/confirmation")) navigate(path);
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin || !("Notification" in window) || !("serviceWorker" in navigator)) {
      return undefined;
    }

    let cancelled = false;

    const register = async () => {
      try {
        if (Notification.permission === "denied") {
          if (!cancelled) setDenied(true);
          return;
        }
        const permission =
          Notification.permission === "granted"
            ? "granted"
            : await Notification.requestPermission();
        if (cancelled) return;
        if (permission !== "granted") {
          setDenied(true);
          return;
        }
        setDenied(false);

        const configRes = await notificationPushService.webConfig();
        const config = configRes?.data?.data;
        if (!config?.apiKey || !config?.vapidKey) return;

        const registration = await navigator.serviceWorker.register(
          `${process.env.PUBLIC_URL || ""}/firebase-messaging-sw.js`,
          { scope: SW_SCOPE }
        );
        const worker = registration.active || registration.waiting || registration.installing;
        if (worker && worker.state !== "activated") {
          await new Promise((resolve) => {
            worker.addEventListener("statechange", () => {
              if (worker.state === "activated") resolve();
            });
          });
        }
        (registration.active || worker)?.postMessage({
          type: "FIREBASE_CONFIG",
          config: {
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
          },
        });
        const loaded = await loadFirebaseMessaging(config, registration);
        if (!loaded?.token || cancelled) return;
        tokenRef.current = loaded.token;
        await notificationPushService.registerDeviceToken({
          token: loaded.token,
          platform: "web",
        });
        console.info("[FCM] device registered");

        loaded.onMessage(loaded.messaging, (payload) => {
          const data = payload.data || {};
          const title = payload.notification?.title || data.title || "Vehicle Confirmation Required";
          const body = payload.notification?.body || data.body || "";
          const tag = data.requestId || "vehicle_confirmation";
          const options = { body, data, tag, renotify: true, requireInteraction: true };
          registration.showNotification(title, options).catch(() => {
            const notice = new Notification(title, { body, tag, data });
            notice.onclick = () => {
              const requestId = String(data.requestId || "").trim();
              navigate(requestId ? `/confirmation/${encodeURIComponent(requestId)}` : "/confirmation?status=PENDING");
              notice.close();
            };
          });
          console.info("[FCM] foreground notification shown");
        });
      } catch (error) {
        console.warn(`[FCM] browser setup failed code=${error?.code || error?.name || "unknown"}`);
      }
    };

    register();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) return undefined;
    const previous = tokenRef.current;
    tokenRef.current = "";
    if (!previous) return undefined;
    notificationPushService.removeDeviceToken(previous).catch(() => {});
    return undefined;
  }, [isAdmin]);

  if (!denied || !isAdmin) return null;

  return (
    <p className="push-permission-banner" role="status">
      Notifications are off. Allow notifications for FastRecovery in your browser or Android settings to receive vehicle confirmation alerts.
    </p>
  );
}
